// Misc
import React, { Component, Fragment, createElement } from 'react'
import PropTypes from 'prop-types'
import { SortableElement, arrayMove } from 'react-sortable-hoc'
import { TransitionGroup } from 'react-transition-group'
import map from 'lodash/map'
import reject from 'lodash/reject'
import includes from 'lodash/includes'
import isNil from 'lodash/isNil'
import pick from 'lodash/pick'
import find from 'lodash/find'
import filter from 'lodash/filter'
import some from 'lodash/some'

// Constants
import * as proposalTypes from 'constants/proposalTypes'
import * as fieldTypes from 'constants/proposalFieldTypes'
import * as customerFieldTypes from 'constants/proposalCustomerFieldTypes'
import * as creationTemplates from 'constants/proposalCreationTemplates'

// Utils
import { createCustomField, getProposalField } from 'utils/proposals'

// Actions
import connectActions from 'utils/connectActions'
import { patchProposalLazy } from 'actions/proposals'

// Selectors
import { connectGetters } from 'react-redux-getters'
import { makeGetProposal } from 'selectors/proposals'
import { isMailChimpConnected, isMindbodyConnected, isWorkflowMaxConnected } from 'selectors/integrations'

// Atoms
import Fade from '@material-ui/core/Fade'
import SortableContainer from 'atoms/SortableContainer'
import Container from 'atoms/Container'
import { AppProgressLoadable } from 'atoms/AppProgress'
import { AppSavingActivator } from 'atoms/AppSaving'
import Heading from 'atoms/Heading'
import Spacer from 'atoms/Spacer'

// Components
import FieldCreationButtons from './FieldCreationButtons'
import CustomerFields from './CustomerFields'
import MailChimpWarning from './MailChimpWarning'
import MindbodyWarning from './MindbodyWarning'
import WorkflowMaxWarning from './WorkflowMaxWarning'

// Field editors
import NameEditor from './Fields/Name'
import CustomerFieldsLabelEditor from './Fields/CustomerFieldsLabel'
import DescriptionEditor from './Fields/Description'
import PriceEditor from './Fields/Price'
import DurationEditor from './Fields/Duration'
import LinkEditor from './Fields/Link'
import InstantLinkEditor from './Fields/InstantLink'
import WhenServiceEditor from './Fields/WhenService'
import WhenEventEditor from './Fields/WhenEvent'
import MessageEditor from './Fields/Message'
import LongTextEditor from './Fields/LongText'
import ShortTextEditor from './Fields/ShortText'
import YesNoEditor from './Fields/YesNo'
import ChoiceEditor from './Fields/Choice'
import SeatsEditor from './Fields/Seats'
import LocationEditor from './Fields/Location'
import LocationChoiceEditor from './Fields/LocationChoice'
import LocationSuggestEditor from './Fields/LocationSuggest'
import NewsletterSubscribeEditor from './Fields/NewsletterSubscribe'
import LiveChatWelcomeMessageEditor from './Fields/LiveChatWelcomeMessage'
import LiveChatAwayMessageEditor from './Fields/LiveChatAwayMessage'
import LiveChatAvailabilityEditor from './Fields/LiveChatAvailability'
import LiveChatRequireUserInfo from './Fields/LiveChatRequireUserInfo'
import DisabledEditor from './Fields/Disabled'

const fieldEditorComponents = {
  [fieldTypes.WHEN_SERVICE]: WhenServiceEditor,
  [fieldTypes.WHEN_EVENT]: WhenEventEditor,
  [fieldTypes.MESSAGE]: MessageEditor,
  [fieldTypes.LONG_TEXT]: LongTextEditor,
  [fieldTypes.SHORT_TEXT]: ShortTextEditor,
  [fieldTypes.YES_NO]: YesNoEditor,
  [fieldTypes.CHOICE]: ChoiceEditor,
  [fieldTypes.DESCRIPTION]: DescriptionEditor,
  [fieldTypes.PRICE]: PriceEditor,
  [fieldTypes.DURATION]: DurationEditor,
  [fieldTypes.LINK]: LinkEditor,
  [fieldTypes.INSTANT_LINK]: InstantLinkEditor,
  [fieldTypes.SEATS]: SeatsEditor,
  [fieldTypes.LOCATION]: LocationEditor,
  [fieldTypes.LOCATION_CHOICE]: LocationChoiceEditor,
  [fieldTypes.LOCATION_SUGGEST]: LocationSuggestEditor,
  [fieldTypes.NEWSLETTER_SUBSCRIBE]: NewsletterSubscribeEditor,
  [fieldTypes.LIVE_CHAT_WELCOME_MESSAGE]: LiveChatWelcomeMessageEditor,
  [fieldTypes.LIVE_CHAT_AWAY_MESSAGE]: LiveChatAwayMessageEditor,
  [fieldTypes.LIVE_CHAT_AVAILABILITY]: LiveChatAvailabilityEditor,
  [fieldTypes.LIVE_CHAT_REQUIRE_USER_INFO]: LiveChatRequireUserInfo,
}

const SortableItem = SortableElement(props => (
  <Fade {...props} />
))

const normalizeFields = (proposal, prevFields = []) => {
  if (!proposal) return []

  let normalizedFields = proposal.data.fields || []

  const removeField = (type) => {
    normalizedFields = reject(normalizedFields, { type })
  }

  const addMissingField = (type, data) => {
    if (!some(normalizedFields, { type })) {
      const alreadyCreated = find(prevFields, { type })
      normalizedFields.push(alreadyCreated || createCustomField(type, data))
    }
  }

  const filterFields = (options) => {
    normalizedFields = filter(normalizedFields, options)
  }

  const isEvent = includes([
    proposalTypes.SINGLE_EVENT,
    proposalTypes.SCHEDULED_EVENT
  ], proposal.type)

  const isLiveChat = some(normalizedFields, {
    type: fieldTypes.LIVE_CHAT
  })

  // Remove all fields except MINDBODY
  if (some(normalizedFields, { type: fieldTypes.MINDBODY })) {
    filterFields({ type: fieldTypes.MINDBODY })
  }
  // Remove all fields except INSTANT_LINK
  if (some(normalizedFields, { type: fieldTypes.INSTANT_LINK })) {
    filterFields({ type: fieldTypes.INSTANT_LINK })
  }
  // Remove all fields except INSTANT_NEWSLETTER_SUBSCRIBE
  if (some(normalizedFields, { type: fieldTypes.INSTANT_NEWSLETTER_SUBSCRIBE })) {
    filterFields({ type: fieldTypes.INSTANT_NEWSLETTER_SUBSCRIBE })
  }
  // Remove all fields except LIVE_CHAT and LIVE_CHAT_WELCOME_MESSAGE
  if (isLiveChat) {
    filterFields(({ type }) => includes([
      fieldTypes.LIVE_CHAT,
      fieldTypes.LIVE_CHAT_WELCOME_MESSAGE,
      fieldTypes.LIVE_CHAT_AVAILABILITY,
      fieldTypes.LIVE_CHAT_AWAY_MESSAGE,
      fieldTypes.LIVE_CHAT_REQUIRE_USER_INFO,
    ], type))
    // Add missing away message
    addMissingField(
      fieldTypes.LIVE_CHAT_AWAY_MESSAGE,
      creationTemplates.liveChatAwayMessageFieldTemplate
    )
  }
  // Remove live chat welcome message for non-livechat proposal
  if (!isLiveChat) {
    removeField(fieldTypes.LIVE_CHAT_WELCOME_MESSAGE)
    removeField(fieldTypes.LIVE_CHAT_AVAILABILITY)
    removeField(fieldTypes.LIVE_CHAT_AWAY_MESSAGE)
  }
  // Remove seats for non-event proposal
  if (!isEvent) {
    removeField(fieldTypes.SEATS)
  }
  // Add when for service
  if (proposal.type === proposalTypes.SCHEDULED_SERVICE) {
    addMissingField(fieldTypes.WHEN_SERVICE)
  }
  // Add when for event
  if (isEvent) {
    addMissingField(fieldTypes.WHEN_EVENT)
  }
  // Add description
  if (!isNil(proposal.description)) {
    addMissingField(fieldTypes.DESCRIPTION)
  }
  // Add price
  if (!isNil(proposal.price)) {
    addMissingField(fieldTypes.PRICE)
  }
  // Add duration
  if (!isNil(proposal.duration)) {
    addMissingField(fieldTypes.DURATION)
  }
  // Add seats for an event
  if (!isNil(proposal.seats) && isEvent) {
    addMissingField(fieldTypes.SEATS)
  }

  return normalizedFields
}

const normalizeCustomerFields = (proposal, prevCustomerFields = []) => {
  if (!proposal) return []

  let normalizedCustomerFields = proposal.data.customerFields || []

  const addMissingCustomerField = (type) => {
    if (!some(normalizedCustomerFields, { type })) {
      const alreadyCreated = find(prevCustomerFields, { type })
      normalizedCustomerFields.push(alreadyCreated || createCustomField(type))
    }
  }

  const removeField = (type) => {
    normalizedCustomerFields = reject(normalizedCustomerFields, { type })
  }

  // Add email
  addMissingCustomerField(customerFieldTypes.EMAIL)

  // Remove workflowmax fields
  if (!getProposalField(proposal, fieldTypes.WORKFLOWMAX)) {
    removeField(customerFieldTypes.WORKFLOWMAX_COMPANY)
  }

  return normalizedCustomerFields
}

const mapGettersToProps = () => {
  const getProposal = makeGetProposal()
  return (state, ownProps) => ({
    proposalGetter: getProposal(state, { proposalUuid: ownProps.proposalUuid }),
    isMailChimpConnectedGetter: isMailChimpConnected(state),
    isMindbodyConnectedGetter: isMindbodyConnected(state),
    isWorkflowMaxConnectedGetter: isWorkflowMaxConnected(state),
  })
}

const mapActionsToProps = {
  patchProposalLazyAction: patchProposalLazy
}

export default
@connectActions(mapActionsToProps)
@connectGetters(mapGettersToProps)
class ProposalEditor extends Component {
  static propTypes = {
    patchProposalLazyAction: PropTypes.object.isRequired,
    proposalUuid: PropTypes.string.isRequired,
    proposalGetter: PropTypes.object.isRequired,
    isMailChimpConnectedGetter: PropTypes.object.isRequired,
    isMindbodyConnectedGetter: PropTypes.object.isRequired,
    isWorkflowMaxConnectedGetter: PropTypes.object.isRequired,
  }

  constructor(props) {
    super()
    const proposal = props.proposalGetter.data
    this.state = {
      normalizedFields: normalizeFields(proposal),
      normalizedCustomerFields: normalizeCustomerFields(proposal),
    }
  }

  static getDerivedStateFromProps = (nextProps, prevState) => {
    const proposal = nextProps.proposalGetter.data
    const { normalizedFields, normalizedCustomerFields } = prevState
    return {
      normalizedFields: normalizeFields(proposal, normalizedFields),
      normalizedCustomerFields: normalizeCustomerFields(proposal, normalizedCustomerFields),
    }
  }

  isInstantLink = () => {
    const { normalizedFields } = this.state
    return some(normalizedFields, {
      type: fieldTypes.INSTANT_LINK
    })
  }

  isInstantNewsletterSubscribe = () => {
    const { normalizedFields } = this.state
    return some(normalizedFields, {
      type: fieldTypes.INSTANT_NEWSLETTER_SUBSCRIBE
    })
  }

  isLiveChat = () => {
    const { normalizedFields } = this.state
    return some(normalizedFields, {
      type: fieldTypes.LIVE_CHAT
    })
  }

  isMindbody = () => {
    const { normalizedFields } = this.state
    return some(normalizedFields, {
      type: fieldTypes.MINDBODY
    })
  }

  isWorkflowMax = () => {
    const { normalizedFields } = this.state
    return some(normalizedFields, {
      type: fieldTypes.WORKFLOWMAX
    })
  }

  updateProposal = async (proposalData = {}) => {
    const editableFields = ['type', 'description', 'price', 'name', 'duration', 'selectOnlyDate', 'beginsAt', 'endsAt', 'seats']
    const { proposalGetter, patchProposalLazyAction } = this.props
    const { normalizedFields, normalizedCustomerFields } = this.state
    const proposal = proposalGetter.data

    const nextProposalData = {
      ...pick(proposal, editableFields),
      ...proposalData,
      data: {
        ...proposal.data,
        fields: normalizedFields,
        customerFields: normalizedCustomerFields,
        ...proposalData.data,
      },
    }

    await patchProposalLazyAction.run({
      params: [proposal.uuid, nextProposalData],
    })
  }

  handleSortEnd = async ({ oldIndex, newIndex }) => {
    if (oldIndex !== newIndex) {
      const { normalizedFields } = this.state
      await this.updateProposal({
        data: {
          fields: arrayMove(normalizedFields, oldIndex, newIndex)
        }
      })
    }
  }

  handleFieldDelete = field => async (proposalData) => {
    const { normalizedFields } = this.state
    await this.updateProposal({
      ...proposalData,
      data: {
        fields: reject(normalizedFields, { key: field.key })
      }
    })
  }

  renderField = (field, index = 0) => {
    const { proposalGetter } = this.props
    const { normalizedFields } = this.state
    const proposal = proposalGetter.data
    const component = fieldEditorComponents[field.type]
    return !!component && (
      <SortableItem key={field.key} index={index}>
        <div>
          { createElement(component, {
            field,
            proposal,
            normalizedFields,
            onProposalUpdate: this.updateProposal,
            onDelete: this.handleFieldDelete(field),
          }) }
        </div>
      </SortableItem>
    )
  }

  renderFields() {
    const {
      proposalGetter, proposalUuid,
      isMailChimpConnectedGetter, isMindbodyConnectedGetter, isWorkflowMaxConnectedGetter
    } = this.props
    const { normalizedFields, normalizedCustomerFields } = this.state
    const proposal = proposalGetter.data
    const isInstantLink = this.isInstantLink()
    const isLiveChat = this.isLiveChat()
    const isMindbody = this.isMindbody()
    const isWorkflowMax = this.isWorkflowMax()
    const isInstantNewsletterSubscribe = this.isInstantNewsletterSubscribe()
    const isDisabled = !!proposal.data.disabled
    const showMoreOptions = !isInstantLink && !isInstantNewsletterSubscribe && !isMindbody
    const showCustomerFields = !isInstantLink && !isInstantNewsletterSubscribe && !isLiveChat
    const showMailChimpWarning = isInstantNewsletterSubscribe && !isMailChimpConnectedGetter.data
    const showMindbodyWarning = isMindbody && !isMindbodyConnectedGetter.data
    const showWorkflowMaxWarning = isWorkflowMax && !isWorkflowMaxConnectedGetter.data
    const showDisabled = isDisabled && !showMailChimpWarning && !showMindbodyWarning
    return (
      <SortableContainer
        axis="y"
        useWindowAsScrollContainer
        onSortEnd={this.handleSortEnd}
      >
        <TransitionGroup exit={false}>
          { showMailChimpWarning
            && (
            <Fade key={`${proposalUuid}-mailChimpWarning`}>
              <div>
                <MailChimpWarning />
                <Spacer />
              </div>
            </Fade>
            )
          }
          { showMindbodyWarning
            && (
            <Fade key={`${proposalUuid}-mindbodyWarning`}>
              <div>
                <MindbodyWarning />
                <Spacer />
              </div>
            </Fade>
            )
          }
          { showWorkflowMaxWarning
            && (
            <Fade key={`${proposalUuid}-workflowMaxWarning`}>
              <div>
                <WorkflowMaxWarning />
                <Spacer />
              </div>
            </Fade>
            )
          }
          <Fade key={`${proposalUuid}-name`}>
            <div>
              <NameEditor
                proposal={proposal}
                onProposalUpdate={this.updateProposal}
              />
            </div>
          </Fade>
          { map(normalizedFields, this.renderField) }
          { showMoreOptions
            && (
            <Fade key={`${proposalUuid}-creationButtons`}>
              <div>
                <FieldCreationButtons
                  proposal={proposal}
                  normalizedFields={normalizedFields}
                  onProposalUpdate={this.updateProposal}
                />
              </div>
            </Fade>
            )
          }
          { showCustomerFields
            && (
            <Fade key={`${proposalUuid}-customerFieldsHeading`}>
              <div>
                <CustomerFieldsLabelEditor
                  proposal={proposal}
                  onProposalUpdate={this.updateProposal}
                />
              </div>
            </Fade>
            )
          }
          { showCustomerFields
            && (
            <Fade key={`${proposalUuid}-customerFields`}>
              <div>
                <CustomerFields
                  proposalFields={normalizedFields}
                  customerFields={normalizedCustomerFields}
                  onProposalUpdate={this.updateProposal}
                />
              </div>
            </Fade>
            )
          }
          { showDisabled
            && (
            <Fade key={`${proposalUuid}-disabled`}>
              <div>
                <DisabledEditor
                  proposal={proposal}
                  onProposalUpdate={this.updateProposal}
                />
              </div>
            </Fade>
            )
          }
        </TransitionGroup>
      </SortableContainer>
    )
  }

  render() {
    const { proposalGetter, patchProposalLazyAction } = this.props
    return (
      <Fragment>
        <AppProgressLoadable loader={proposalGetter} />
        { patchProposalLazyAction.isPending && <AppSavingActivator /> }
        { proposalGetter.isSucceded
          && (
          <Fragment>
            <Heading
              primary="Edit"
              secondary={
                this.isInstantNewsletterSubscribe()
                || this.isInstantLink()
                || this.isMindbody()
                || this.isLiveChat()
                  ? 'Change the wording'
                  : 'Change the wording & add, delete and reorder fields'
              }
            />
            <Spacer />
            <Container>
              { this.renderFields() }
            </Container>
          </Fragment>
          )
        }
      </Fragment>
    )
  }
}
